/**
 * Shared utilities for liming soil data components.
 * Used by both LimeSoilSummary and LimeSoilDataDialog.
 */

import LimeProgramLabInputRow from "../../controllers/LimeProgramLabInputRow"
import LimeProgramLabInputs from "../../controllers/LimeProgramLabInputs"
import LabInputUnit from "../../controllers/LabInputUnit"
import SampleDepth from "../../controllers/SampleDepth"
import NullableMeasureValue from "../../controllers/NullableMeasureValue"
import MeasurementUnit from "../../controllers/MeasurementUnit"
import {SamplePoint} from "../../components/GeoMap"
import {ELEMENT_SPECIFIC_GRAVITY} from "./staticdata"
import {ColumnMatcher, matchArray} from "../program/CSVParse"

// Row with internal ID for tracking in local state
export interface RowWithId extends LimeProgramLabInputRow {
    _id: number
}

// Element types that use specific gravity conversion
export type ConvertibleElement = 'Ca' | 'Mg' | 'K' | 'Na'

/**
 * Create a NullableMeasureValue from a number.
 * mg/kg and ppm are 1:1 equivalent, so metric and imperial are the same.
 */
export function toMeasureValue(value: number): NullableMeasureValue {
    return {metric: value, imperial: value}
}

/**
 * Convert meq/100g values to mg/kg by multiplying by specific gravity.
 * Some regions (e.g., Uruguay) report K, Na, Ca, Mg in meq/100g instead of mg/kg.
 */
export function convertMeq100gToMgKg(value: number, element: ConvertibleElement): number {
    return value * ELEMENT_SPECIFIC_GRAVITY[element]
}

/**
 * Convert mg/kg back to meq/100g values by dividing by specific gravity.
 * Used for display when showing stored values to meq/100g users.
 */
export function convertMgKgToMeq100g(value: number, element: ConvertibleElement): number {
    return value / ELEMENT_SPECIFIC_GRAVITY[element]
}

/**
 * Check if region uses meq/100g lab input units.
 */
export function isMeq100g(labInputUnit: LabInputUnit | undefined): boolean {
    return labInputUnit === LabInputUnit.Meq100g
}

/**
 * Parse sample depth from CSV string value.
 * Handles various formats: "SubSoil", "sub_soil", "Subsoil", etc.
 */
export function parseSampleDepth(value: string): SampleDepth {
    const normalized = value.toLowerCase().replace(/[\s_()-]/g, '')
    if (normalized === 'subsoil') return SampleDepth.SubSoil
    if (normalized === 'topsoilmicros') return SampleDepth.TopSoilMicros
    return SampleDepth.TopSoil
}

/**
 * Create an empty row with default values.
 */
export function createEmptyRow(id: number): RowWithId {
    return {
        _id: id,
        landRef: '',
        referenceNr: '',
        phH2O: 0,
        pBrayI: toMeasureValue(0),
        k: toMeasureValue(0),
        na: toMeasureValue(0),
        ca: toMeasureValue(0),
        mg: toMeasureValue(0),
        exchH: 0,
        sulfur: toMeasureValue(0),
        density: 1.2,
        caPercent: null,
        mgPercent: null,
        kPercent: null,
        naPercent: null,
        acidSat: null,
        sValue: null,
        cec: null,
        caMgRatio: null,
        caMgKRatio: null,
        mgKRatio: null,
        naKRatio: null,
        latitude: null,
        longitude: null,
        sampleDepth: SampleDepth.TopSoil,
        limeTypeOverride: null,
        limeNeedOverride: null,
        gypsumNeedOverride: null,
        kNeedOverride: null,
        pNeedOverride: null,
        elementalSulphurOverride: null,
        outputs: null
    }
}

/**
 * Convert LimeProgramLabInputs to RowWithId array for local state management.
 */
export function toRowsWithId(data: LimeProgramLabInputs): RowWithId[] {
    return data.rows.map((row, index) => ({...row, _id: index}))
}

/**
 * Convert RowWithId array back to LimeProgramLabInputs for saving.
 */
export function toLabInputs(data: RowWithId[]): LimeProgramLabInputs {
    return {rows: data.map(({_id, ...row}) => row)}
}

/**
 * Convert rows to sample points for the GeoMap component.
 */
export function rowsToSamplePoints(rows: RowWithId[]): SamplePoint[] {
    return rows
        .filter(row => row.latitude !== null && row.longitude !== null)
        .map(row => ({
            latitude: row.latitude!,
            longitude: row.longitude!,
            label: row.referenceNr || row.landRef,
            sampleDepth: row.sampleDepth === SampleDepth.SubSoil ? 'subsoil' : 'topsoil'
        }))
}

/**
 * Format a nullable number for display.
 */
export function formatValue(value: number | null): string {
    if (value === null) return '-'
    return value.toFixed(2)
}

/**
 * Format a NullableMeasureValue for display.
 */
export function formatMeasure(system: MeasurementUnit, value: NullableMeasureValue): string {
    const v = system === MeasurementUnit.METRIC ? value.metric : value.imperial
    if (v === null || v === undefined) return '-'
    return v.toFixed(2)
}

/**
 * Format K, Na, Ca, Mg values - converts back to meq/100g for display if needed.
 */
export function formatMeasureElement(
    system: MeasurementUnit,
    value: NullableMeasureValue,
    element: ConvertibleElement,
    meq100gMode: boolean
): string {
    const v = system === MeasurementUnit.METRIC ? value.metric : value.imperial
    if (v === null || v === undefined) return '-'
    const displayValue = meq100gMode ? convertMgKgToMeq100g(v, element) : v
    return displayValue.toFixed(2)
}

/**
 * Convert a row's element values for editing (mg/kg to meq/100g if needed).
 */
export function convertRowForEditing(row: RowWithId, meq100gMode: boolean): RowWithId {
    if (!meq100gMode) return {...row}

    return {
        ...row,
        k: toMeasureValue(convertMgKgToMeq100g(row.k.metric || 0, 'K')),
        na: toMeasureValue(convertMgKgToMeq100g(row.na.metric || 0, 'Na')),
        ca: toMeasureValue(convertMgKgToMeq100g(row.ca.metric || 0, 'Ca')),
        mg: toMeasureValue(convertMgKgToMeq100g(row.mg.metric || 0, 'Mg'))
    }
}

/**
 * Convert a row's element values for saving (meq/100g to mg/kg if needed).
 */
export function convertRowForSaving(row: RowWithId, meq100gMode: boolean): RowWithId {
    if (!meq100gMode) return row

    return {
        ...row,
        k: toMeasureValue(convertMeq100gToMgKg(row.k.metric || 0, 'K')),
        na: toMeasureValue(convertMeq100gToMgKg(row.na.metric || 0, 'Na')),
        ca: toMeasureValue(convertMeq100gToMgKg(row.ca.metric || 0, 'Ca')),
        mg: toMeasureValue(convertMeq100gToMgKg(row.mg.metric || 0, 'Mg'))
    }
}

// CSV column name aliases for parsing
export const CSV_COLUMN_ALIASES = {
    landRef: ['Land Ref', 'LandRef', 'Block', 'Land ref'],
    referenceNr: ['Reference Nr', 'ReferenceNr', 'Ref', 'Sample#', 'Reference nr.', 'Reference'],
    phH2O: ['pH(H2O)', 'pH (H2O)', 'pH_H2O'],
    pBrayI: ['P Bray I', 'P_BrayI', 'P', 'PBray', 'P Bray'],
    k: ['K', 'K mg/kg', 'K (mg/kg)', 'K (ppm)', 'K (meq/100g)'],
    na: ['Na', 'Na mg/kg', 'Na (mg/kg)', 'Na (ppm)', 'Na (meq/100g)'],
    ca: ['Ca', 'Ca mg/kg', 'Ca (mg/kg)', 'Ca (ppm)', 'Ca (meq/100g)'],
    mg: ['Mg', 'Mg mg/kg', 'Mg (mg/kg)', 'Mg (ppm)', 'Mg (meq/100g)'],
    exchH: ['Exch H', 'Exch H+', 'ExchH', 'H', 'OUT H+ (cmol(+)/kg)', 'Exch Acid KCL'],
    sulfur: ['S', 'Sulfur', 'S (mg/kg)', 'S (ppm)', 'S Am AC'],
    density: ['Density', 'BD', 'Density (g/cm3)'],
    caPercent: ['%Ca', 'Ca (%)', 'Ca%'],
    mgPercent: ['%Mg', 'Mg (%)', 'Mg%'],
    kPercent: ['%K', 'K (%)', 'K%'],
    naPercent: ['%Na', 'Na (%)', 'Na%'],
    acidSat: ['Acid Sat', 'Acid Sat.', 'AcidSat', 'SOUR.V (%)', 'Acid Sat AmAc'],
    sValue: ['S-Value', 'SValue', 'S-Value (cmol(+)/kg)'],
    cec: ['CEC', 'T', 'T (cmol(+)/kg)', 'T-Value AmAc'],
    caMgRatio: ['Ca:Mg', 'CaMgRatio', 'Ca:Mg (1.5-4.5)'],
    caMgKRatio: ['(Ca+Mg)/K', 'CaMgKRatio', '(Ca+Mg)/K (10.0-20.0)', '(Ca+Mg) / K'],
    mgKRatio: ['Mg:K', 'MgKRatio', 'Mg:K (3.0-4.0)'],
    naKRatio: ['Na:K', 'NaKRatio'],
    latitude: ['Latitude', 'Lat', 'Y'],
    longitude: ['Longitude', 'Lon', 'Long', 'X'],
    sampleDepth: ['Sample Depth', 'SampleDepth'],
    limeTypeOverride: ['Lime Type Override', 'LimeTypeOverride'],
    limeNeedOverride: ['Lime Need Override', 'LimeNeedOverride'],
    gypsumNeedOverride: ['Gypsum Need Override', 'GypsumNeedOverride'],
    kNeedOverride: ['K Need Override', 'KNeedOverride'],
    pNeedOverride: ['P Need Override', 'PNeedOverride'],
    elementalSulphurOverride: ['Elemental Sulphur Override', 'ElementalSulphurOverride', 'Elem S Override']
} as const

/**
 * Get a value from a CSV row using column aliases.
 */
export function getCsvValue(row: Record<string, string>, aliases: readonly string[]): string {
    for (const alias of aliases) {
        const value = row[alias]
        if (value !== undefined && value !== '') {
            return value
        }
    }
    return ''
}
/**
 * Build column matchers for CSV parsing using parseCSVFile utility.
 * @param meq100gMode - Whether to convert K, Na, Ca, Mg from meq/100g to mg/kg
 */
export function buildLimeSoilColumnMatchers(meq100gMode: boolean): ColumnMatcher<RowWithId>[] {
    const parseNum = (v: string) => parseFloat(v) || 0
    const parseNumNullable = (v: string) => parseFloat(v) || null
    const parseNumDefault = (v: string, def: number) => parseFloat(v) || def

    return [
        {match: matchArray(...CSV_COLUMN_ALIASES.landRef), populate: (v, e) => e.landRef = v},
        {match: matchArray(...CSV_COLUMN_ALIASES.referenceNr), populate: (v, e) => e.referenceNr = v},
        {match: matchArray(...CSV_COLUMN_ALIASES.phH2O), populate: (v, e) => e.phH2O = parseNum(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.pBrayI), populate: (v, e) => e.pBrayI = toMeasureValue(parseNum(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.k), populate: (v, e) => {
            const val = parseNum(v)
            e.k = toMeasureValue(meq100gMode ? convertMeq100gToMgKg(val, 'K') : val)
        }},
        {match: matchArray(...CSV_COLUMN_ALIASES.na), populate: (v, e) => {
            const val = parseNum(v)
            e.na = toMeasureValue(meq100gMode ? convertMeq100gToMgKg(val, 'Na') : val)
        }},
        {match: matchArray(...CSV_COLUMN_ALIASES.ca), populate: (v, e) => {
            const val = parseNum(v)
            e.ca = toMeasureValue(meq100gMode ? convertMeq100gToMgKg(val, 'Ca') : val)
        }},
        {match: matchArray(...CSV_COLUMN_ALIASES.mg), populate: (v, e) => {
            const val = parseNum(v)
            e.mg = toMeasureValue(meq100gMode ? convertMeq100gToMgKg(val, 'Mg') : val)
        }},
        {match: matchArray(...CSV_COLUMN_ALIASES.exchH), populate: (v, e) => e.exchH = parseNum(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.sulfur), populate: (v, e) => e.sulfur = toMeasureValue(parseNum(v))},
        {match: matchArray(...CSV_COLUMN_ALIASES.density), populate: (v, e) => e.density = parseNumDefault(v, 1.2)},
        {match: matchArray(...CSV_COLUMN_ALIASES.caPercent), populate: (v, e) => e.caPercent = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.mgPercent), populate: (v, e) => e.mgPercent = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.kPercent), populate: (v, e) => e.kPercent = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.naPercent), populate: (v, e) => e.naPercent = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.acidSat), populate: (v, e) => e.acidSat = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.sValue), populate: (v, e) => e.sValue = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.cec), populate: (v, e) => e.cec = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.caMgRatio), populate: (v, e) => e.caMgRatio = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.caMgKRatio), populate: (v, e) => e.caMgKRatio = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.mgKRatio), populate: (v, e) => e.mgKRatio = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.naKRatio), populate: (v, e) => e.naKRatio = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.latitude), populate: (v, e) => e.latitude = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.longitude), populate: (v, e) => e.longitude = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.sampleDepth), populate: (v, e) => e.sampleDepth = parseSampleDepth(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.limeTypeOverride), populate: (v, e) => e.limeTypeOverride = v || null},
        {match: matchArray(...CSV_COLUMN_ALIASES.limeNeedOverride), populate: (v, e) => e.limeNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.gypsumNeedOverride), populate: (v, e) => e.gypsumNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.kNeedOverride), populate: (v, e) => e.kNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.pNeedOverride), populate: (v, e) => e.pNeedOverride = parseNumNullable(v)},
        {match: matchArray(...CSV_COLUMN_ALIASES.elementalSulphurOverride), populate: (v, e) => e.elementalSulphurOverride = parseNumNullable(v)},
    ]
}
